import { cn } from "@/lib/cn";
import { useSession } from "@/components/providers/session-provider";
import { useSidebar } from "@/components/providers/sidebar-provider";
import { useRegistrations } from "@/components/providers/registrations-provider";
import { useNotifications } from "@/components/providers/notifications-provider";
import { useWorkspace } from "@/components/providers/workspace-provider";
import { useAdminDashboard } from "@/components/providers/admin-dashboard-provider";
import { useNavigationArrangement } from "@/components/providers/navigation-provider";
import { InstallNavRow } from "@/components/install-prompt";
import { apiFetch } from "@/lib/api";
import type {
  SidebarActivityCountsDto,
  SidebarActivitySection,
} from "@/lib/types";
import {
  ADMIN_NAV_SECTIONS,
  NAV_SECTIONS,
  TROPHY_ICON,
  arrangeNavSections,
  filterCampaignFeatureSections,
  isActive,
  isAdminArea,
  isNewPage,
  resolveHref,
  type NavItem,
  type NavSection,
} from "./nav-config";
import { ChevronDown, ChevronsLeft, LogOut, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

/** One nav row. Collapses to a centred icon with a hover tooltip. */
function NavRow({
  item,
  href,
  collapsed,
  active,
  count,
  countLabel,
  onNavigate,
}: {
  item: NavItem;
  href: string;
  collapsed: boolean;
  active: boolean;
  count?: number;
  countLabel?: string;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  return (
    <li>
      <Link
        to={href}
        target="_self"
        onClick={onNavigate}
        aria-current={active ? "page" : undefined}
        title={collapsed ? item.label : undefined}
        className={cn(
          "group relative flex items-center rounded-xl text-sm font-medium transition-all duration-200",
          collapsed ? "h-11 w-11 justify-center" : "h-11 gap-3 px-3",
          active
            ? "bg-white/12 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
            : "text-white/65 hover:bg-white/8 hover:text-white",
        )}
      >
        {/* Active indicator rail */}
        <span
          aria-hidden="true"
          className={cn(
            "absolute left-0 top-1/2 w-1 -translate-y-1/2 rounded-r-full bg-[var(--brand-accent)] transition-all duration-200",
            active ? "h-6 opacity-100" : "h-0 opacity-0",
          )}
        />
        <Icon
          className={cn(
            "h-[18px] w-[18px] shrink-0 transition-transform duration-200",
            active ? "text-[var(--brand-accent)]" : "group-hover:scale-110",
          )}
        />
        {!collapsed && <span className="truncate">{item.label}</span>}

        {/* A page nobody knows to look for needs a reason to be clicked, but only while it is
            actually news - the window closes on its own. It sits before the unread count so a row
            carrying both reads "New" first, and is hidden on the collapsed rail, where there is no
            label for it to qualify. */}
        {isNewPage(item) && !collapsed && (
          <span className="ml-auto rounded-full bg-[var(--brand-accent)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
            New
          </span>
        )}

        {count !== undefined && count > 0 && (
          <span
            aria-label={countLabel ?? `${count} unseen items`}
            className={cn(
              "flex items-center justify-center rounded-full bg-[var(--brand-accent)] font-bold text-white",
              collapsed
                ? "absolute right-1 top-1 h-4 min-w-4 px-1 text-[9px]"
                : "ml-auto h-5 min-w-5 px-1.5 text-[10px]",
            )}
          >
            <span aria-hidden="true">{count > 99 ? "99+" : count}</span>
            <span className="sr-only">{countLabel ?? `${count} unseen items`}</span>
          </span>
        )}

        {/* Tooltip when collapsed */}
        {collapsed && (
          <span
            role="tooltip"
            className="pointer-events-none absolute left-full z-50 ml-3 hidden whitespace-nowrap rounded-lg bg-[var(--ink)] px-2.5 py-1.5 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 lg:block"
          >
            {item.label}
          </span>
        )}
      </Link>
    </li>
  );
}

/** A titled group of rows; admin is collapsible. */
function NavGroup({
  section,
  collapsed,
  pathname,
  slug,
  unreadCount,
  pendingAccessRequestCount,
  sidebarActivityCounts,
  onMarkSidebarActivitySeen,
  onNavigate,
  hasWorkspaceAccess,
  hasWorkspaceManagement,
  hasRegistration,
  isAdmin,
}: {
  section: NavSection;
  collapsed: boolean;
  pathname: string;
  slug: string | null;
  unreadCount: number;
  pendingAccessRequestCount: number;
  sidebarActivityCounts: SidebarActivityCountsDto;
  onMarkSidebarActivitySeen: (section: SidebarActivitySection, pathname?: string) => void;
  onNavigate?: () => void;
  hasWorkspaceAccess: boolean;
  hasWorkspaceManagement: boolean;
  hasRegistration: boolean;
  isAdmin: boolean;
}) {
  const rows = section.items
    .map((item) => ({ item, href: resolveHref(item, slug) }))
    .filter(({ item }) => !item.adminOnly || isAdmin)
    .filter(({ item }) => !item.workspaceAccessOnly || hasWorkspaceAccess)
    .filter(({ item }) => !item.workspaceManagerOnly || hasWorkspaceManagement)
    .filter(({ item }) => !item.requiresRegistration || hasRegistration)
    .filter((r): r is { item: NavItem; href: string } => r.href !== null);

  const containsActive = rows.some((r) =>
    isActive(r.href, pathname, r.item.exact, r.item.activePrefixes),
  );

  const [open, setOpen] = useState(section.defaultOpen ?? true);

  // Auto-expand a collapsible group when the user navigates into it.
  useEffect(() => {
    if (containsActive) setOpen(true);
  }, [containsActive]);

  if (rows.length === 0) return null;

  const countFor = (item: NavItem) => {
    if (item.badge === "notifications") return unreadCount;
    if (item.badge === "accessRequests") return pendingAccessRequestCount;
    if (item.badge === "sidebarActivity" && item.activitySection) {
      const key = item.activitySection.toLowerCase() as keyof SidebarActivityCountsDto;
      return sidebarActivityCounts[key];
    }
    return undefined;
  };

  const markSeenFor = (item: NavItem, href: string) => {
    if (item.badge === "sidebarActivity" && item.activitySection) {
      onMarkSidebarActivitySeen(item.activitySection, href);
    }
    onNavigate?.();
  };

  const body = (
    <ul className={cn("flex flex-col gap-1", collapsed && "items-center")}>
      {rows.map(({ item, href }) => {
        const count = countFor(item);
        return (
          <NavRow
            key={href}
            item={item}
            href={href}
            collapsed={collapsed}
            active={isActive(href, pathname, item.exact, item.activePrefixes)}
            count={count}
            countLabel={count ? `${count} unseen ${item.label} items` : undefined}
            onNavigate={() => markSeenFor(item, href)}
          />
        );
      })}
    </ul>
  );

  return (
    <div className="flex flex-col gap-1.5">
      {!collapsed &&
        section.label !== "" &&
        (section.collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="flex items-center gap-1.5 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-white/40 transition-colors hover:text-white/70"
          >
            {section.label}
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 transition-transform duration-200",
                open ? "rotate-0" : "-rotate-90",
              )}
            />
          </button>
        ) : (
          <p className="px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-white/40">
            {section.label}
          </p>
        ))}

      {collapsed && (
        <span aria-hidden="true" className="my-1 h-px w-8 bg-white/10" />
      )}

      {section.collapsible && !collapsed ? (
        <div
          className={cn(
            "grid transition-all duration-300 ease-out",
            open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
          )}
        >
          <div className="overflow-hidden">{body}</div>
        </div>
      ) : (
        body
      )}
    </div>
  );
}

/** Shared inner content for both the desktop rail and the mobile drawer. */
function SidebarContent({
  collapsed,
  onNavigate,
  onCloseDrawer,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
  onCloseDrawer?: () => void;
}) {
  const { me, isAdmin, signout } = useSession();
  const { workspaces } = useWorkspace();
  const hasWorkspaceAccess =
    isAdmin || me?.workspaceAccessEnabled === true;
  const hasWorkspaceManagement =
    isAdmin ||
    workspaces.some(workspace =>
      ["Organizer", "Administrator", "Owner"].includes(workspace.role),
    );
  const { unreadCount } = useNotifications();
  const { pendingAccessRequestCount } = useAdminDashboard();
  const { activeSlug, registrations } = useRegistrations();
  const hasRegistration = registrations.length > 0;
  const activeRegistration = useMemo(
    () =>
      registrations.find((registration) => registration.challengeSlug === activeSlug) ?? null,
    [activeSlug, registrations],
  );
  const { toggleCollapsed } = useSidebar();
  const { arrangementFor } = useNavigationArrangement();
  const { pathname } = useLocation();
  const [sidebarActivityCounts, setSidebarActivityCounts] =
    useState<SidebarActivityCountsDto>({ community: 0, memories: 0, kudos: 0 });
  const lastMarkedRef = useRef<string | null>(null);

  const sidebarActivityPath = useMemo(
    () =>
      activeSlug
        ? `/api/sidebar-activity?challengeSlug=${encodeURIComponent(activeSlug)}`
        : null,
    [activeSlug],
  );

  const refreshSidebarActivity = useCallback(async () => {
    if (!sidebarActivityPath) {
      setSidebarActivityCounts({ community: 0, memories: 0, kudos: 0 });
      return;
    }
    try {
      const counts = await apiFetch<SidebarActivityCountsDto>(sidebarActivityPath, {
        suppressAuthRedirect: true,
      });
      setSidebarActivityCounts(counts);
    } catch {
      setSidebarActivityCounts({ community: 0, memories: 0, kudos: 0 });
    }
  }, [sidebarActivityPath]);

  useEffect(() => {
    void refreshSidebarActivity();
  }, [refreshSidebarActivity]);

  const markSidebarActivitySeen = useCallback(
    (section: SidebarActivitySection, seenPathname = pathname) => {
      if (!activeSlug) return;
      lastMarkedRef.current = `${activeSlug}:${section}:${seenPathname}`;
      const key = section.toLowerCase() as keyof SidebarActivityCountsDto;
      setSidebarActivityCounts((current) => ({ ...current, [key]: 0 }));
      void apiFetch<SidebarActivityCountsDto>(
        `/api/sidebar-activity/${section}/seen?challengeSlug=${encodeURIComponent(activeSlug)}`,
        { method: "POST", suppressAuthRedirect: true },
      )
        .then(setSidebarActivityCounts)
        .catch(() => void refreshSidebarActivity());
    },
    [activeSlug, pathname, refreshSidebarActivity],
  );

  const sections = useMemo(() => {
    const inAdminArea = isAdminArea(pathname);
    const source = inAdminArea ? ADMIN_NAV_SECTIONS : NAV_SECTIONS;
    const arrangement = arrangementFor(
      inAdminArea ? "AdminSidebar" : "ParticipantSidebar",
    );
    // The campaign gate runs before the workspace arrangement so a hidden or renamed row is
    // decided once, on rows the campaign has actually opened.
    return arrangeNavSections(
      filterCampaignFeatureSections(source, activeRegistration),
      arrangement,
    ).filter((s) => !s.adminOnly || isAdmin);
  }, [activeRegistration, isAdmin, pathname, arrangementFor]);

  const name = me?.displayName ?? "";

  useEffect(() => {
    if (!activeSlug) return;
    const activeItem = sections
      .flatMap((section) => section.items)
      .find((item) =>
        item.badge === "sidebarActivity"
        && item.activitySection
        && isActive(
          resolveHref(item, activeSlug) ?? item.href,
          pathname,
          item.exact,
          item.activePrefixes,
        ),
      );
    if (!activeItem?.activitySection) return;
    const marker = `${activeSlug}:${activeItem.activitySection}:${pathname}`;
    if (lastMarkedRef.current === marker) return;
    lastMarkedRef.current = marker;
    markSidebarActivitySeen(activeItem.activitySection);
  }, [activeSlug, markSidebarActivitySeen, pathname, sections]);

  return (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div
        className={cn(
          "flex h-16 shrink-0 items-center border-b border-white/10",
          collapsed ? "justify-center px-2" : "gap-2.5 px-4",
        )}
      >
        {collapsed ? (
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label="Expand navigation"
            title="Expand"
            className="flex h-10 w-10 items-center justify-center rounded-xl text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          >
            <ChevronsLeft className="h-5 w-5 rotate-180" />
          </button>
        ) : (
          <>
            <Link
              to="/dashboard"
              onClick={onNavigate}
              className="flex items-center gap-2.5 overflow-hidden"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--brand-accent)] text-white shadow-[var(--shadow-accent)]">
                <TROPHY_ICON className="h-[18px] w-[18px]" />
              </span>
              <span className="text-[15px] font-extrabold leading-none tracking-tight text-white">
                STEP<span className="text-[var(--brand-accent)]">-</span>TEMBER
              </span>
            </Link>

            {/* Desktop collapses the rail; the mobile drawer closes instead. */}
            {onCloseDrawer ? (
              <button
                type="button"
                onClick={onCloseDrawer}
                aria-label="Close navigation"
                className="ml-auto flex h-9 w-9 items-center justify-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={toggleCollapsed}
                aria-label="Collapse navigation"
                title="Collapse"
                className="ml-auto hidden h-9 w-9 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white lg:flex"
              >
                <ChevronsLeft className="h-5 w-5" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Nav */}
      <nav
        aria-label="Main"
        className={cn(
          "scroll-slim flex-1 overflow-y-auto py-4",
          collapsed ? "px-2" : "px-3",
        )}
      >
        <div className="flex flex-col gap-5">
          {sections.map((section) => (
            <NavGroup
              key={section.id}
              section={section}
              collapsed={collapsed}
              pathname={pathname}
              slug={activeSlug}
              unreadCount={unreadCount}
              pendingAccessRequestCount={pendingAccessRequestCount}
              sidebarActivityCounts={sidebarActivityCounts}
              onMarkSidebarActivitySeen={markSidebarActivitySeen}
              onNavigate={onNavigate}
              hasWorkspaceAccess={hasWorkspaceAccess}
              hasWorkspaceManagement={hasWorkspaceManagement}
              hasRegistration={hasRegistration}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      </nav>

      {/* User + collapse control */}
      <div
        className={cn(
          "shrink-0 border-t border-white/10 py-3",
          collapsed ? "px-2" : "px-3",
        )}
      >
        <InstallNavRow collapsed={collapsed} onNavigate={onNavigate} />
        <div
          className={cn(
            "flex items-center rounded-xl bg-white/5 transition-colors",
            collapsed ? "justify-center p-2" : "gap-2.5 p-2.5",
          )}
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-primary-soft)] text-[11px] font-bold text-white">
            {initials(name || "?")}
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-white">
                  {name}
                </span>
                <span className="block truncate text-[11px] text-white/50">
                  {isAdmin ? "Administrator" : "Participant"}
                </span>
              </span>
              <button
                type="button"
                onClick={() => void signout()}
                aria-label="Sign out"
                title="Sign out"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function Sidebar() {
  const { collapsed, drawerOpen, closeDrawer } = useSidebar();
  const { me } = useSession();

  if (me?.authenticated !== true) return null;

  return (
    <>
      {/* Desktop rail */}
      <aside
        data-collapsed={collapsed ? "true" : "false"}
        className={cn(
          "brand-gradient fixed inset-y-0 left-0 z-40 hidden shrink-0 border-r border-white/10 transition-[width] duration-300 ease-out lg:block",
          collapsed ? "w-[var(--sidebar-w-collapsed)]" : "w-[var(--sidebar-w)]",
        )}
      >
        <SidebarContent collapsed={collapsed} />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={closeDrawer}
            className="absolute inset-0 bg-[var(--ink)]/60 backdrop-blur-sm"
          />
          <div className="brand-gradient animate-slide-in-left absolute inset-y-0 left-0 w-[min(19rem,85vw)] border-r border-white/10 shadow-2xl">
            <SidebarContent
              collapsed={false}
              onNavigate={closeDrawer}
              onCloseDrawer={closeDrawer}
            />
          </div>
        </div>
      )}
    </>
  );
}
